import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-duplicates
import { addDays, addYears, format, parse } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { ptBR } from 'date-fns/locale';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { UseCampanhaHook } from 'hooks/campanha-hook';
import { CampanhaValues } from 'models/campanhas';
import campanhaService from 'services/campanha-service/campanha-service';
import { ErrorModal } from '@fundacao/shared-components/esm/ie/';
import { useHistory } from 'react-router-dom';
import { UseCampanhaContext } from 'context/campanha-context';
import { CampaignDataInputProps } from 'models/campaign-mkt';
import * as S from './style';

const DataInput = ({
  label = 'Data de início',
}: CampaignDataInputProps): JSX.Element => {
  const history = useHistory();
  const { formCampanhaValues, formCampanhaErrors } = UseCampanhaContext();
  const { handleInputChange, handleInputError } = UseCampanhaHook();
  const [blockedDates, setBlockedDates] = useState<Date[]>();
  const [callAttemptCount, setCallAttemptCount] = useState(0);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const minimumDate = addDays(new Date(), 1);
  const dateValue = formCampanhaValues[CampanhaValues.DATA_DE_INICIO];
  const hasError = formCampanhaErrors[CampanhaValues.DATA_DE_INICIO];
  const calendarOpenRef = useRef(false);
  const dateValueRef = useRef(dateValue);

  dateValueRef.current = dateValue;

  const validateDate = () =>
    handleInputError(CampanhaValues.DATA_DE_INICIO, !dateValueRef.current);

  const getBlockedDates = async () => {
    try {
      const dates = await campanhaService.getBlockedDates();
      setBlockedDates(
        dates.map((item) => parse(item.data, 'dd-MM-yyyy', new Date())),
      );
      setShowErrorModal(false);
    } catch (error) {
      setShowErrorModal(true);
    }
  };

  useEffect(() => {
    if (callAttemptCount < 2) {
      getBlockedDates();
    } else {
      history.push('/mkt-campanhas');
    }
  }, [callAttemptCount, history]);

  const formatDateForInput = () =>
    dateValue ? String(dateValue).replace(/-/g, '/') : '';

  const datePickerButton = (
    <S.DateButton type="button" $error={hasError} $filled={Boolean(dateValue)}>
      <S.DateLabel>{label}</S.DateLabel>
      <span>{formatDateForInput()}</span>
      <S.CalendarIcon aria-hidden="true" />
    </S.DateButton>
  );

  return (
    <>
      <S.DatePickerWrapper>
        <DatePicker
          selected={
            dateValue ? parse(dateValue, 'dd-MM-yyyy', new Date()) : null
          }
          locale={ptBR}
          dateFormat="dd/MM/yyyy"
          minDate={minimumDate}
          maxDate={addYears(new Date(), 1)}
          fixedHeight
          excludeDates={blockedDates}
          filterDate={(date) => date.getDay() !== 0 && date.getDay() !== 6}
          onCalendarClose={() => {
            calendarOpenRef.current = false;
            validateDate();
          }}
          onCalendarOpen={() => {
            calendarOpenRef.current = true;
          }}
          customInput={datePickerButton}
          onChange={(date) => {
            if (!date) return;
            const formattedDate = format(date, 'dd-MM-yyyy');
            dateValueRef.current = formattedDate;
            handleInputChange(CampanhaValues.DATA_DE_INICIO, formattedDate);
            handleInputError(CampanhaValues.DATA_DE_INICIO, false);
          }}
          onBlur={() => {
            window.setTimeout(() => {
              if (!calendarOpenRef.current) validateDate();
            }, 0);
          }}
        />
      </S.DatePickerWrapper>
      {hasError && (
        <S.DateError>
          Informe uma data a partir de amanhã (exceto finais de semana e
          feriados)
        </S.DateError>
      )}
      {showErrorModal && (
        <ErrorModal
          isOpen={showErrorModal}
          title="Tivemos um problema em carregar este conteúdo"
          bodyMessage="Atualize a página ou volte para a página inicial."
          primaryButton={{
            label: 'Atualizar',
            onClick: () => setCallAttemptCount(callAttemptCount + 1),
          }}
          secondaryButton={{
            label: 'Voltar',
            onClick: () => history.push('/mkt-campanhas'),
          }}
        />
      )}
    </>
  );
};

export default DataInput;
